import Axios from 'axios'
import type {
  TRateSimChatMessageBody,
  TRateSimChatMessageResponse,
  TSimTableChatDto,
  TSimTableChatSummaryDto
} from '@homepal/api-contract'

import { NodeData } from 'types/transformation/types/nodes'

import { getStreamAuth } from 'redux/api/internal/Chat'
import { parseAuthenticationHeader } from 'helpers/Functions'
import { signAwsRequest } from 'utils/awsSigV4'
import { getWithRedirect } from 'utils/axios'

const IS_DEV = process.env.NODE_ENV === 'development'

const BASE_URL = IS_DEV
  ? 'http://localhost:3990/'
  : process.env.REACT_APP_HOMEPAL_MDM_API_PROD

export type TSimChatMessage = {
  role: 'user' | 'assistant'
  content: string
}

export type TSimChatStreamBody = {
  message: string
  history: TSimChatMessage[]
  nodes: NodeData[]

  // The stored chat this message continues; absent on the first message.
  chat_id?: string
}

export type TSimChatToolStep = {
  tool: string
  input: Record<string, unknown>
}

export type TSimChatTransformation = {
  nodes: NodeData[]
  summary: string | null
}

// What the reply did to the board, counted by the server rather than told by
// the assistant.
export type TSimChatBoardChanges = {
  proposals_accepted: number
  proposals_rejected: number
}

export type TSimChatResult = {
  reply: string
  board: TSimChatBoardChanges | null

  // The stored chat and reply, for rating the reply. The id of the reply is
  // null when the server could not store it.
  chat_id: string | null
  message_id: string | null
}

export type TSimChatStreamCallbacks = {
  onText: (delta: string) => void
  onToolStart: (step: TSimChatToolStep) => void
  onToolEnd: (step: TSimChatToolStep) => void
  onTransformation: (payload: TSimChatTransformation) => void
  onDone: (result: TSimChatResult) => void
  onError: (message: string) => void

  // The stream closed before the reply finished (e.g. the server timed out).
  onInterrupted: () => void
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function parseBoardChanges(value: unknown): TSimChatBoardChanges | null {
  return isObject(value)
    && typeof value.proposals_accepted === 'number'
    && typeof value.proposals_rejected === 'number'
    ? {
        proposals_accepted: value.proposals_accepted,
        proposals_rejected: value.proposals_rejected
      }
    : null
}

function parseFrame(frame: string): { event: string; data: string } | null {
  const lines = frame.split('\n')
  let event = 'message'
  let data = ''

  for (const line of lines) {
    if (line.startsWith('event: ')) event = line.slice(7).trim()
    else if (line.startsWith('data: ')) data += line.slice(6)
  }

  if (!data) return null

  return { event, data }
}

function dispatchFrame(
  event: string,
  parsed: unknown,
  callbacks: TSimChatStreamCallbacks
) {
  switch (event) {
  case 'tool_start':
    if (isObject(parsed) && typeof parsed.tool === 'string') {
      callbacks.onToolStart(parsed as TSimChatToolStep)
    }
    break
  case 'tool_end':
    if (isObject(parsed) && typeof parsed.tool === 'string') {
      callbacks.onToolEnd(parsed as TSimChatToolStep)
    }
    break
  case 'text':
    if (isObject(parsed) && typeof parsed.delta === 'string') {
      callbacks.onText(parsed.delta)
    }
    break
  case 'transformation':
    if (isObject(parsed) && Array.isArray(parsed.nodes)) {
      callbacks.onTransformation({
        nodes: parsed.nodes as NodeData[],
        summary: typeof parsed.summary === 'string' ? parsed.summary : null
      })
    }
    break
  case 'done':
    if (isObject(parsed) && typeof parsed.reply === 'string') {
      callbacks.onDone({
        reply: parsed.reply,
        board: parseBoardChanges(parsed.board),
        chat_id: typeof parsed.chat_id === 'string' ? parsed.chat_id : null,
        message_id:
          typeof parsed.message_id === 'string' ? parsed.message_id : null
      })
    }
    break
  case 'error':
    if (isObject(parsed) && typeof parsed.message === 'string') {
      callbacks.onError(parsed.message)
    }
    break
  default:
    break
  }
}

// Opens the streaming response. Locally we hit the Express dev route with
// cookies; in production the SIM chat streams through the same SigV4-signed
// chatStream Function URL as the metric chat (API Gateway can't stream and
// times out at ~30 s), selected with type: 'sim' in the body.
async function openStream(
  tableId: string,
  body: TSimChatStreamBody,
  signal?: AbortSignal
): Promise<Response> {
  // The exact bytes sent must match what we hash for the signature.
  const payload = JSON.stringify({
    data: { type: 'sim', table_id: tableId, ...body }
  })

  if (IS_DEV) {
    return fetch(`${BASE_URL}v4/data-products/tables/${tableId}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream'
      },
      body: payload,
      credentials: 'include',
      signal
    })
  }

  const { token, url, region, credentials } = await getStreamAuth()

  const signed = await signAwsRequest({
    url,
    method: 'POST',
    body: payload,
    region,
    service: 'lambda',
    credentials
  })

  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'x-chat-stream-token': token,
      ...signed
    },
    body: payload,
    signal
  })
}

/**
 * Posts a modelling instruction for a SIM table and consumes the SSE stream.
 * Stateless: pass the full message history plus the current board nodes on
 * every call.
 */
export async function streamSimChat(
  tableId: string,
  body: TSimChatStreamBody,
  callbacks: TSimChatStreamCallbacks,
  signal?: AbortSignal
): Promise<void> {
  let response: Response

  try {
    response = await openStream(tableId, body, signal)
  } catch (e) {
    callbacks.onError(e instanceof Error ? e.message : String(e))

    return
  }

  if (!response.ok || !response.body) {
    callbacks.onError(`HTTP ${response.status}`)

    return
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let finished = false

  try {
    for (;;) {
      const { done, value } = await reader.read()

      if (done) break

      buffer += decoder.decode(value, { stream: true })

      // SSE frames are separated by a blank line.
      let separatorIdx = buffer.indexOf('\n\n')

      while (separatorIdx !== -1) {
        const frame = buffer.slice(0, separatorIdx)

        buffer = buffer.slice(separatorIdx + 2)

        const parsedFrame = parseFrame(frame)

        if (parsedFrame) {
          try {
            const parsed: unknown = JSON.parse(parsedFrame.data)

            dispatchFrame(parsedFrame.event, parsed, callbacks)

            if (parsedFrame.event === 'done' || parsedFrame.event === 'error') {
              finished = true
            }
          } catch {
            // Ignore frames with malformed JSON payloads.
          }
        }

        separatorIdx = buffer.indexOf('\n\n')
      }
    }
  } catch {
    // A dropped connection ends the stream like a server timeout does.
  }

  if (!finished && !signal?.aborted) callbacks.onInterrupted()
}

// Rates one reply of a stored SIM chat. Write-once: the backend rejects a
// second rating. The response is small enough to never be redirected to S3.
export async function rateSimChatMessage(
  tableId: string,
  chatId: string,
  messageId: string,
  body: TRateSimChatMessageBody
) {
  return Axios.post<TRateSimChatMessageResponse>(
    `${BASE_URL}v4/data-products/tables/${tableId}/chat/${chatId}/messages/${messageId}/feedback`,
    { data: body },
    parseAuthenticationHeader()
  )
}

// The signed-in user's own chats on the table, newest activity first.
export async function getSimTableChats(tableId: string) {
  return getWithRedirect<TSimTableChatSummaryDto[]>(
    `${BASE_URL}v4/data-products/tables/${tableId}/chats`
  )
}

// One of the user's own chats with every turn, for resuming it in the panel.
export async function getSimTableChat(tableId: string, chatId: string) {
  return getWithRedirect<TSimTableChatDto>(
    `${BASE_URL}v4/data-products/tables/${tableId}/chats/${chatId}`
  )
}
